import { z } from 'zod'
import { apiRequest } from './client'

/**
 * Client for the admin background-jobs API (`/api/v1/admin/jobs`).
 *
 * Lists every managed background job (arq crons + Slack notifications) with
 * its runtime control state, and lets an admin pause/resume a job or set a
 * schedulable cron's schedule. Admin / Superadmin only — a 403 surfaces with
 * `err.status === 403`.
 */

export const scheduleConfigSchema = z.object({
  unit: z.enum(['hour', 'day', 'week', 'month', 'year']),
  value: z.number().int(),
  dayOfWeek: z.number().int().nullable(),
  dayOfMonth: z.number().int().nullable(),
  month: z.number().int().nullable(),
  hour: z.number().int(),
})
export type ScheduleConfig = z.infer<typeof scheduleConfigSchema>

export const jobRowSchema = z.object({
  name: z.string(),
  kind: z.enum(['cron', 'notification']),
  label: z.string(),
  description: z.string(),
  details: z.string(),
  schedule: z.string(),
  schedulable: z.boolean(),
  enabled: z.boolean(),
  scheduleConfig: scheduleConfigSchema.nullable(),
  lastRunAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
})
export type JobRow = z.infer<typeof jobRowSchema>

const jobsListSchema = z.object({ jobs: z.array(jobRowSchema) })

export async function listJobs(): Promise<JobRow[]> {
  const raw = await apiRequest<unknown>('/api/v1/admin/jobs')
  return jobsListSchema.parse(raw).jobs
}

export interface PatchJobInput {
  enabled: boolean
  /** Present only when changing a schedulable cron's schedule. */
  schedule?: ScheduleConfig
}

export async function patchJob(name: string, input: PatchJobInput): Promise<JobRow> {
  const raw = await apiRequest<unknown>(`/api/v1/admin/jobs/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    body: input,
  })
  return jobRowSchema.parse(raw)
}
